import {roundGa4Currency} from '@amblique/king-living/app/analytics/ga4/currency';
import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';
import {getPriceData, getProductEffectivePrice} from '@amblique/king-living/app/utils/product-utils';
import {resolveAbsoluteImageUrl} from '@amblique/king-living/app/utils/responsive-image-src';

const normalizeGa4ItemPrice = (value) => (
  value != null ? roundGa4Currency(value) ?? 0 : value
);

const CATEGORY_KEYS = ['item_category', 'item_category2', 'item_category3'];

const mapGa4CategoryLabels = (labels = []) => labels.slice(0, 3).reduce((categories, label, index) => {
  const trimmed = String(label ?? '').trim();

  if (trimmed) {
    categories[CATEGORY_KEYS[index]] = trimmed;
  }

  return categories;
}, {});

const mapItemCategories = (categoryTree = []) => mapGa4CategoryLabels(
  categoryTree.map((category) => category?.name || category?.id)
);

const parseGa4CategoryPathLabels = (productOrItem) => {
  const rawPath =
    productOrItem?.c_ga4CategoryPath ??
    productOrItem?.representedProduct?.c_ga4CategoryPath;

  if (!rawPath) {
    return [];
  }

  try {
    const parsed = typeof rawPath === 'string' ? JSON.parse(rawPath) : rawPath;

    if (Array.isArray(parsed)) {
      return parsed.map((name) => String(name).trim()).filter(Boolean);
    }
  } catch {
    // Ignore malformed JSON from hook payload.
  }

  return [];
};

const resolveCategoryTreeFromProduct = (productOrItem) => {
  const labels = parseGa4CategoryPathLabels(productOrItem);

  if (labels.length) {
    return labels.map((name) => ({name}));
  }

  const fallbackName =
    productOrItem?.c_primaryCategoryName ??
    productOrItem?.representedProduct?.c_primaryCategoryName;

  return fallbackName ? [{name: String(fallbackName).trim()}] : [];
};

/** Cart events — `c_ga4CategoryPath` on product; omit category keys when path is missing. */
const mapGa4CartItemCategories = (productOrItem) => (
  mapGa4CategoryLabels(parseGa4CategoryPathLabels(productOrItem))
);

const stripEmptyGa4ItemFields = (item) => {
  if (!item) {
    return item;
  }

  const next = {...item};

  [...CATEGORY_KEYS, 'item_category4'].forEach((key) => {
    if (next[key] == null || next[key] === '') {
      delete next[key];
    }
  });

  return next;
};

export const applyGa4CartItemCategories = (item, productOrItem) => {
  if (!item) {
    return item;
  }

  const next = {...item};

  CATEGORY_KEYS.forEach((key) => {
    delete next[key];
  });
  delete next.item_category4;

  return stripEmptyGa4ItemFields({...next, ...mapGa4CartItemCategories(productOrItem)});
};

export const resolveGa4ItemId = (productOrItem) => (
  productOrItem?.productId ||
  productOrItem?.id ||
  productOrItem?.representedProduct?.id ||
  ''
);

const resolveItemName = (productOrItem) => (
  productOrItem?.name ||
  productOrItem?.productName ||
  productOrItem?.representedProduct?.name ||
  ''
);

const resolveVariantTarget = (productOrItem) => {
  if (!productOrItem) {
    return null;
  }

  if (!productOrItem?.variationValues && productOrItem?.variants?.length === 1) {
    return productOrItem.variants[0];
  }

  return productOrItem;
};

/** KINDW-326 — `item_variant` is the variant product name when the line is a variant product. */
export const resolveAddToCartItemVariant = (productOrItem) => {
  if (!productOrItem) {
    return '';
  }

  const target = resolveVariantTarget(productOrItem) || productOrItem;
  const hasVariationValues =
    target.variationValues && Object.keys(target.variationValues).length > 0;

  if (!hasVariationValues) {
    return '';
  }

  return String(target.name || target.productName || '').trim();
};

const resolveItemImage = (productOrItem) => {
  const fromMaster = getMasterTileImages(productOrItem).find(
    (img) => img.mediaType !== 'video' && (img.disBaseLink || img.link)
  );

  if (fromMaster?.disBaseLink || fromMaster?.link) {
    return resolveAbsoluteImageUrl(fromMaster.disBaseLink || fromMaster.link);
  }

  const fallbackImage = productOrItem?.image ?? productOrItem?.representedProduct?.image;

  return resolveAbsoluteImageUrl(fallbackImage?.disBaseLink || fallbackImage?.link || '');
};

export const toGa4Item = (productOrItem, options = {}) => {
  if (!productOrItem) {
    return null;
  }

  const itemId = resolveGa4ItemId(productOrItem);

  if (!itemId) {
    return null;
  }

  const {currency, quantity = 1, index, categoryTree, listItem = false, includeQuantity = true} = options;
  const priceData = getPriceData(productOrItem, {quantity: 1, ...(currency && {currency})});
  const fromProduct = resolveCategoryTreeFromProduct(productOrItem);
  const categories = mapItemCategories(fromProduct.length ? fromProduct : categoryTree ?? []);
  const price = normalizeGa4ItemPrice(priceData?.currentPrice ?? productOrItem?.price);
  const itemVariant = resolveAddToCartItemVariant(productOrItem);
  const image = resolveItemImage(productOrItem);
  const itemName = resolveItemName(productOrItem);

  const item = {
    item_id: itemId,
    item_name: itemName,
    page_source: 'storefront',
    ...categories,
    ...(listItem ? {} : {price, ...(includeQuantity ? {quantity} : {})}),
    ...(listItem && price != null ? {price} : {}),
    ...(itemVariant ? {item_variant: itemVariant} : {}),
    image: image || '',
    ...(typeof index === 'number' ? {index} : {}),
  };

  return stripEmptyGa4ItemFields(item);
};

export const toGa4AddToCartItem = (productOrItem, options = {}) => {
  const item = toGa4Item(productOrItem, options);

  if (!item) {
    return null;
  }

  const effectivePrice = getProductEffectivePrice(productOrItem);

  if (effectivePrice != null) {
    item.price = normalizeGa4ItemPrice(effectivePrice);
  }

  const itemVariant = resolveAddToCartItemVariant(productOrItem);

  if (itemVariant) {
    item.item_variant = itemVariant;
  }

  item.image = resolveItemImage(productOrItem) || '';

  return stripEmptyGa4ItemFields(item);
};

export const toGa4AddToCartItemPartial = ({
  itemId,
  itemName,
  price = 0,
  quantity = 1,
  categoryTree,
  itemVariant,
  image,
}) => {
  if (!itemId) {
    return null;
  }

  return stripEmptyGa4ItemFields({
    item_id: String(itemId),
    item_name: String(itemName || itemId),
    page_source: 'storefront',
    price: normalizeGa4ItemPrice(price ?? 0),
    quantity: quantity ?? 1,
    ...mapItemCategories(categoryTree ?? []),
    ...(itemVariant ? {item_variant: itemVariant} : {}),
    image: image || '',
  });
};
